import WalletConnectProvider from "@walletconnect/ethereum-provider";
import { constants } from "config";
import {
  Connector,
  ConnectorDispatch,
  MockWalletConnectProvider,
} from "../types";

const parseChainId = (chainId: string | number) =>
  typeof chainId === "number" ? chainId : Number.parseInt(chainId, 10);

class WalletConnectConnector extends Connector {
  public provider: MockWalletConnectProvider | undefined;

  /**
   * Store any in-progress immediate connection to use if manual connection is attempted
   */
  private immediateConnection?: Promise<void>;

  constructor(dispatch: ConnectorDispatch) {
    super(dispatch);

    // Reconnect if possible, note that WalletConnect doesn't need to check for disconnect cookie here
    this.connectImmediately();
  }

  // Store event handlers so they can be removed by name in this.disconnect()
  private disconnectHandler = (error: Error | undefined): void => {
    this.dispatch({ type: "error", error });
  };

  private chainChangedHandler = (chainId: string | number): void => {
    this.dispatch({ type: "update", chainId: parseChainId(chainId) });
  };

  private accountsChangedHandler = (accounts: string[]): void => {
    this.dispatch({ type: "update", accounts });
  };

  private async initialize(): Promise<void> {
    if (this.immediateConnection) return this.immediateConnection;

    await (this.immediateConnection = Promise.resolve(
      new WalletConnectProvider({
        chainId: constants.chainId,
        rpc: { [constants.chainId]: constants.alchemyUrl as string },
      })
    ).then((provider) => {
      this.provider = provider as MockWalletConnectProvider;

      this.provider.on("disconnect", this.disconnectHandler);

      this.provider.on("chainChanged", this.chainChangedHandler);

      this.provider.on("accountsChanged", this.accountsChangedHandler);
    }));

    return undefined;
  }

  public async connectImmediately(): Promise<void> {
    await this.initialize();

    if (this.provider?.connected) {
      try {
        // Can't use Promise.all[] here because parallel fetches default to
        // a chainId of 1 regardless of the connected wallet
        const accounts = (await this.provider.request({
          method: "eth_accounts",
        })) as string[];
        const chainId = parseChainId(
          (await this.provider.request({ method: "eth_chainId" })) as
            | string
            | number
        );

        if (accounts.length) {
          this.dispatch({ type: "update", accounts, chainId });
        } else {
          throw new Error("No accounts from WalletConnect provider");
        }
      } catch {
        // Fail silently, most commonly because user hasn't connected yet
        // console.debug("Couldn't connect immediately", error);
        this.dispatch({ type: "reset" });
      }
    }
  }

  public async connect(): Promise<void> {
    this.dispatch({ type: "startConnecting" });

    await this.initialize();

    // In case we're already connected
    if (this.provider?.connected) {
      return;
    }

    try {
      // Can't use Promise.all[] here because parallel fetches default to
      // a chainId of 1 regardless of the connected wallet
      const accounts = (await this.provider?.request({
        method: "eth_requestAccounts",
      })) as string[];
      const chainId = parseChainId(
        (await this.provider?.request({ method: "eth_chainId" })) as
          | string
          | number
      );

      this.dispatch({ type: "update", chainId, accounts });
    } catch (error) {
      if ((error as Error).message === "User closed modal") {
        // HACK: For some reason, WC Modal won't reopen after user closes it. Running disconnect
        // should reset everything to initial state so that it can be opened again.
        await this.disconnect();
      } else {
        this.dispatch({ type: "error", error: error as Error });
      }
    }
  }

  public async disconnect(): Promise<void> {
    this.provider?.off("disconnect", this.disconnectHandler);
    this.provider?.off("chainChanged", this.chainChangedHandler);
    this.provider?.off("accountsChanged", this.accountsChangedHandler);

    await this.provider?.disconnect();

    this.dispatch({ type: "reset" });
    this.provider = undefined;
    this.immediateConnection = undefined;
  }
}

export { WalletConnectConnector };
